import type { Task } from "@/engine/types";
import { clampGridWidth, GRID_BORDER_PX, NAME_MIN_WIDTH } from "./grid-splitter";

/** Matches WbsGrid name cell: `paddingLeft: 8 + depth * 16`. */
export const NAME_PAD_LEFT_PX = 8;
export const NAME_INDENT_PX = 16;
/** Right padding from `px-2` after the left padding override. */
export const NAME_PAD_RIGHT_PX = 8;
/** Chevron button is `size-4`; leaf spacer/diamond is smaller — use the larger. */
export const NAME_ICON_PX = 16;
/** `gap-1` between icon and title. */
export const NAME_ICON_GAP_PX = 4;
/** Issue dot (`size-1.5`) plus its `gap-1`. Hover-only inspector button is ignored. */
export const NAME_ISSUE_PX = 10;
/** Slack so the longest title does not sit flush against the splitter. */
export const NAME_FIT_SAFETY_PX = 16;

export const NAME_COLUMN_FONT =
  '400 13px -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Inter", system-ui, "Apple SD Gothic Neo", "Noto Sans KR", sans-serif';

export interface NameFitRow {
  name: string;
  depth: number;
  hasIssue?: boolean;
}

export function nameColumnChromeWidth(depth: number, hasIssue = false): number {
  return (
    NAME_PAD_LEFT_PX +
    Math.max(0, depth) * NAME_INDENT_PX +
    NAME_ICON_PX +
    NAME_ICON_GAP_PX +
    NAME_PAD_RIGHT_PX +
    (hasIssue ? NAME_ISSUE_PX : 0) +
    NAME_FIT_SAFETY_PX
  );
}

export function nameRowFitWidth(depth: number, textWidth: number, hasIssue = false): number {
  return Math.ceil(Math.max(0, textWidth)) + nameColumnChromeWidth(depth, hasIssue);
}

export function bestFitNameColumnWidth(
  rows: readonly NameFitRow[],
  measureText: (text: string) => number,
): number {
  let max = NAME_MIN_WIDTH;
  for (const row of rows) {
    const label = row.name.trim() ? row.name : "이름 없음";
    max = Math.max(max, nameRowFitWidth(row.depth, measureText(label), row.hasIssue === true));
  }
  return max;
}

/** Full table width so a fitted name column does not clip the schedule columns. */
export function bestFitGridWidth(nameWidth: number, fixedNonNameWidth: number): number {
  return clampGridWidth(fixedNonNameWidth + Math.max(NAME_MIN_WIDTH, nameWidth) + GRID_BORDER_PX);
}

/**
 * Name column fills leftover pane width only after every enabled column already fits.
 * Narrower panes keep the name floor so dragging reveals the next column instead of stretching the title.
 */
export function visibleNameColumnWidth(gridWidth: number, fixedNonNameWidth: number): number {
  const leftoverIfFull = gridWidth - fixedNonNameWidth - GRID_BORDER_PX;
  return leftoverIfFull >= NAME_MIN_WIDTH ? leftoverIfFull : NAME_MIN_WIDTH;
}

export function nameFitRowsFromProject(
  rowIds: readonly string[],
  tasks: readonly Task[],
  schedule: { tasks: ReadonlyMap<string, { depth: number }> },
  issueTaskIds: ReadonlySet<string>,
): NameFitRow[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  return rowIds.map((id) => ({
    name: byId.get(id)?.name ?? "",
    depth: schedule.tasks.get(id)?.depth ?? 0,
    hasIssue: issueTaskIds.has(id),
  }));
}

export function issueTaskIdSet(issues: ReadonlyArray<{ taskIds: readonly string[] }>): Set<string> {
  const ids = new Set<string>();
  for (const issue of issues) {
    for (const id of issue.taskIds) ids.add(id);
  }
  return ids;
}

/** CJK ≈ 1em, Latin ≈ 0.54em at 13px. Used when canvas is unavailable. */
export function estimateNameTextWidth(text: string): number {
  let width = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    width += code > 0x2e80 ? 13 : 7;
  }
  return width;
}

let measureCanvas: HTMLCanvasElement | null = null;

export function nameColumnFontFromElement(el: Element | null): string {
  if (!el || typeof getComputedStyle === "undefined") return NAME_COLUMN_FONT;
  const style = getComputedStyle(el);
  return `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
}

export function measureNameText(text: string, font = NAME_COLUMN_FONT): number {
  if (typeof document === "undefined") return estimateNameTextWidth(text);
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  if (!ctx) return estimateNameTextWidth(text);
  ctx.font = font;
  return ctx.measureText(text).width;
}
